"use client"

import styled from "@emotion/styled"
import { AccordionSummary, Button, Drawer } from "@mui/material"
import { ArrowLeftIcon, ArrowRightIcon } from "@mui/x-date-pickers"
import moment from "moment"
import { useTranslations } from "next-intl"
import Image from "next/image"
import { useState } from "react"
import { Desktop, Mobile, NewText, NotAvailableIcon, useIsDesktop } from "ui"
import { sharedColors } from "ui/theme/colors"
import { Apartment, getUmbracoMediaUrl } from "utils"
import { choosenProject } from "utils/gtm"
import { ProjectsMessages } from "../../Projects"
import DrawerInner from "./DrawerInner"

const UpperBox = styled.div`
  display: flex;
  align-items: stretch;
  gap: 16px;
`

const ImageWrapper = styled.div<{ isTheApartmentTaken: boolean }>`
  position: relative;
  img {
    filter: ${(props) => (props.isTheApartmentTaken ? "grayscale(100%)" : "none")};
  }
`

const ItemBox = styled.div<{
  open: boolean
  $isDisabled: boolean
  // eslint-disable-next-line no-unused-vars
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => void
}>`
  cursor: pointer;
  pointer-events: ${(props) => (props.$isDisabled ? "none" : "auto")};
  font-family: var(--rag), sans-serif !important;
  * {
    font-family: inherit !important;
  }
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 16px;
  /* height: 120px; */
  width: 100%;
  padding: 8px;
  ${(props) => props.theme.breakpoints.up("md")} {
    gap: 24px;
  }
  border-radius: 16px;
  box-shadow: 0px 4px 12px 0px #00000029;
  &:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: 2px;
  }
`

const StyledImage = styled(Image)`
  border-radius: 8px;
  overflow: hidden;
  position: relative;
`

const ContentPart = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  align-items: stretch;
  justify-content: space-between;
  width: 100%;
  ${(props) => props.theme.breakpoints.up("md")} {
    justify-content: end;
    flex-direction: row;
    padding: 8px;
  }
`

const TopContent = styled.div`
  display: flex;
  align-items: center;
  justify-self: baseline;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  padding-right: 24px;
  ${(props) => props.theme.breakpoints.up("md")} {
    width: 200px;
    min-width: 200px;
    flex-shrink: 0;
  }
`

const MiddleContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 1;
  min-width: 0;
  ${(props) => props.theme.breakpoints.up("md")} {
    border-inline-start: 2px solid #f7f7f7;
    padding: 0 17.83px;
  }
`

const LargeDivider = styled.div`
  display: none;
  ${(props) => props.theme.breakpoints.up("md")} {
    display: block;
  }
  height: 100%;
  width: 2px;
  background-color: #f7f7f7;
`

const BottomContent = styled.div<{
  hiddenMobile?: boolean
  hiddenDesktop?: boolean
}>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  ${(props) => props.hiddenMobile && props.theme.breakpoints.down("md")} {
    display: none;
  }

  ${(props) => props.hiddenDesktop && props.theme.breakpoints.up("md")} {
    display: none;
    gap: 24px;
  }

  ${(props) => props.theme.breakpoints.up("md")} {
    flex-shrink: 0;
    width: 280px;
  }
`

const BottomContentMobileDivider = styled.div`
  width: 1px;
  height: 20px;
  background-color: ${({ theme }) => theme.colors.lightGrey};
  ${(props) => props.theme.breakpoints.up("md")} {
    display: none;
  }
`

const StyledFlex = styled.div<{ flexStart?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: center;

  ${(props) => props.theme.breakpoints.up("md")} {
    flex: 1;

    align-items: ${({ flexStart }) => (flexStart ? "flex-start" : "center")};
    min-width: 0;
  }
`

const StyledFlexRow = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`

const StyledButton = styled(Button)`
  display: flex;
  align-items: center;
  text-decoration: none;
  gap: 6px;
  justify-content: flex-end;
  font-weight: 400;
  width: auto;
  height: min-content;
  padding: 0;
  font-size: 14px;
  line-height: 20.16px;
  width: 24px;
  height: 24px;
  min-height: 0;
  min-width: 0;
  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 16px;
    line-height: 23.04px;
    justify-content: space-between;
    gap: 3px;
  }
  :hover {
    text-decoration: none;
  }
`

const ButtonPlaceholder = styled.div`
  width: 24px;
  height: 24px;
`

const StyledDrawer = styled(Drawer)`
  /* padding-left: 16px; */
  box-shadow: none;
  border: none;
  ::before {
    content: none;
  }
`
const StyledAccordionSummary = styled(AccordionSummary)`
  cursor: auto !important;
  padding: 0;

  .MuiAccordionSummary-content {
    margin: 0;
  }
`

const StyledEmptyDiv = styled.div`
  height: 104px;
  width: 106px;
  ${(props) => props.theme.breakpoints.up("md")} {
    height: 104px;
    width: 128px;
  }
`

const ReservedTag = styled.div`
  position: absolute;
  top: 8px;
  z-index: 1;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 4px;
  justify-content: center;
  background-color: #fff;
  border-radius: 22px;
  border: 1px solid #c41407;
  color: #c41407;

  font-weight: 300;
  line-height: 150%;
  font-size: 12px;
  width: calc(100% - 6px);
  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 14px;
    width: calc(100% - 16px);
  }
`

const formatPrice = (number?: number, hideFriction?: boolean) => {
  if (!number) return "0"
  return new Intl.NumberFormat("en-US", {
    style: "decimal",
    minimumFractionDigits: hideFriction ? 0 : 2,
    maximumFractionDigits: hideFriction ? 0 : 2,
  }).format(number)
}

type Props = {
  apartment: Apartment
  formSubjects: Record<string, string>
  messages: ProjectsMessages
  bmbyId?: number
  status?: any
  imageGallery?: {
    apartmentThumbnail?: string
    apartmentGallery?: string[]
    numberOfRooms?: number
  }
  locale: string
  companyId?: number
  buildingCharacteristicsText?: string
  bmbyMeetingId?: string | number
  fontFamilyVariable?: string
  phone?: string
  rentProjectInterestedNum?: number
  rentalProjects?: { name: string; id: number; rooms: string[] }[]
  cmsProjectLeadIds?: Record<string, number>
  preSelectedProject?: string
  leaveDetailsButtonText?: string
}
function ApartmentDrawerItem({
  apartment,
  formSubjects,
  messages,
  bmbyId,
  status,
  locale,
  imageGallery,
  companyId,
  buildingCharacteristicsText,
  bmbyMeetingId,
  fontFamilyVariable,
  phone,
  rentProjectInterestedNum,
  rentalProjects,
  cmsProjectLeadIds,
  preSelectedProject,
  leaveDetailsButtonText,
}: Props) {
  const isDesktop = useIsDesktop()
  const [open, setOpen] = useState<boolean>(false)
  const t = useTranslations("AccordionDetails")
  type ItemEvent =
    | React.MouseEvent<HTMLDivElement>
    | React.KeyboardEvent<HTMLDivElement>
  const handleOpen = (event: ItemEvent) => {
    choosenProject("מידע נוסף", apartment?.propertyTitle)
    event.stopPropagation() // Prevent accordion toggle
    setOpen(true)
  }
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault()
      handleOpen(event)
    }
  }
  const handleClose = (event: React.MouseEvent<HTMLButtonElement>) => {
    choosenProject("סגירה", apartment?.propertyTitle)
    event.stopPropagation() // Prevent accordion toggle
    setOpen(false)
  }
  const formattedPrice = formatPrice(apartment.price, true)
  const image = imageGallery?.apartmentThumbnail || apartment.propertyImage

  const isTheApartmentTaken = apartment.moreInfoContentHTML === "משוריין"

  return (
    <>
      <StyledAccordionSummary>
        <ItemBox
          open={open}
          aria-disabled={isTheApartmentTaken}
          $isDisabled={isTheApartmentTaken}
          onClick={handleOpen}
          onKeyDown={handleKeyDown}
          role="button"
          tabIndex={isTheApartmentTaken ? -1 : 0}
          aria-label={`${apartment.propertyTitle} ${t("building")} ${apartment.buildingTitle}`}
        >
          <UpperBox>
            <ImageWrapper isTheApartmentTaken={isTheApartmentTaken}>
              {isTheApartmentTaken && (
                <ReservedTag>
                  <NotAvailableIcon />
                  {t("apartmentTaken")}
                </ReservedTag>
              )}
              <Desktop>
                {image ? (
                  <StyledImage
                    alt={t("apartmentImage")}
                    src={getUmbracoMediaUrl(image)}
                    height={104}
                    width={128}
                  />
                ) : (
                  <StyledEmptyDiv />
                )}
              </Desktop>
              <Mobile>
                {image ? (
                  <StyledImage
                    alt={t("apartmentImage")}
                    src={getUmbracoMediaUrl(image)}
                    height={104}
                    width={106}
                  />
                ) : (
                  <StyledEmptyDiv />
                )}
              </Mobile>
            </ImageWrapper>
            <ContentPart>
              <TopContent>
                <NewText
                  component={"span"}
                  desktopFontSize={20}
                  desktopLineHeight={"1.2"}
                  sx={{
                    minHeight: {
                      xs: "26px",
                      md: "29px",
                    },
                  }}
                  mobileFontSize={18}
                  mobileLineHeight={"1.2"}
                  desktopFontWeight={700}
                  color={sharedColors.newBlack}
                >
                  {apartment.propertyTitle} {locale === "he" ? "מס׳" : "No."}{" "}
                  {apartment.apartmentNumber} {t("building")} {apartment.buildingTitle}
                </NewText>
                <NewText
                  component={"span"}
                  desktopFontSize={18}
                  desktopLineHeight={"normal"}
                  mobileFontSize={14}
                  mobileLineHeight={"normal"}
                  desktopFontWeight={400}
                  sx={{
                    minHeight: {
                      xs: "20px",
                      md: "26px",
                    },
                  }}
                  color={sharedColors.newBlack}
                >
                  {apartment.address}
                </NewText>
              </TopContent>
              <MiddleContent>
                <StyledFlex>
                  <NewText
                    flex={1}
                    component={"p"}
                    desktopFontSize={20}
                    desktopLineHeight={"1.2"}
                    mobileFontSize={16}
                    desktopFontWeight={700}
                    mobileFontWeight={700}
                    mobileLineHeight={"1.2"}
                    color={sharedColors.newBlack}
                    columnMobile={true}
                  >
                    {apartment.numberOfRooms}
                  </NewText>
                  <NewText
                    flex={1}
                    component={"p"}
                    desktopFontSize={18}
                    desktopLineHeight={"normal"}
                    mobileFontSize={14}
                    mobileLineHeight={"normal"}
                    mobileFontWeight={400}
                    desktopFontWeight={400}
                    columnMobile={true}
                    sx={{
                      color: {
                        xs: `${sharedColors.newBlack} !important`,
                        md: `${sharedColors.black} !important`,
                      },
                    }}
                  >
                    {t("numberOfRooms")}
                  </NewText>
                </StyledFlex>
                <StyledFlex>
                  <NewText
                    flex={1}
                    component={"p"}
                    desktopFontSize={20}
                    desktopLineHeight={"1.2"}
                    mobileFontSize={16}
                    desktopFontWeight={700}
                    mobileFontWeight={700}
                    mobileLineHeight={"1.2"}
                    color={sharedColors.newBlack}
                    columnMobile={true}
                  >
                    {apartment.floor}
                  </NewText>
                  <NewText
                    flex={1}
                    component={"p"}
                    desktopFontSize={18}
                    desktopLineHeight={"normal"}
                    mobileFontSize={14}
                    mobileLineHeight={"normal"}
                    mobileFontWeight={400}
                    desktopFontWeight={400}
                    columnMobile={true}
                    sx={{
                      color: {
                        xs: `${sharedColors.newBlack} !important`,
                        md: `${sharedColors.black} !important`,
                      },
                    }}
                  >
                    {t("floor")}
                  </NewText>
                </StyledFlex>
                <StyledFlex>
                  <NewText
                    flex={1}
                    component={"p"}
                    desktopFontSize={20}
                    desktopLineHeight={"1.2"}
                    mobileFontSize={16}
                    desktopFontWeight={700}
                    mobileFontWeight={700}
                    mobileLineHeight={"1.2"}
                    color={sharedColors.newBlack}
                    columnMobile={true}
                  >
                    {apartment.areaInSquareMeters}
                  </NewText>
                  <NewText
                    flex={1}
                    component={"p"}
                    desktopFontSize={18}
                    desktopLineHeight={"normal"}
                    mobileFontSize={14}
                    mobileLineHeight={"normal"}
                    mobileFontWeight={400}
                    desktopFontWeight={400}
                    columnMobile={true}
                    sx={{
                      color: {
                        xs: `${sharedColors.newBlack} !important`,
                        md: `${sharedColors.black} !important`,
                      },
                    }}
                  >
                    {t("sqm")}
                  </NewText>
                </StyledFlex>
                <StyledFlex>
                  <NewText
                    flex={1}
                    component={"p"}
                    desktopFontSize={20}
                    desktopLineHeight={"1.2"}
                    mobileFontSize={16}
                    desktopFontWeight={700}
                    mobileFontWeight={700}
                    mobileLineHeight={"1.2"}
                    color={sharedColors.newBlack}
                    columnMobile={true}
                  >
                    {apartment.balconySquareMeter}
                  </NewText>
                  <NewText
                    flex={1}
                    component={"p"}
                    desktopFontSize={18}
                    desktopLineHeight={"normal"}
                    mobileFontSize={14}
                    mobileLineHeight={"normal"}
                    mobileFontWeight={400}
                    desktopFontWeight={400}
                    columnMobile={true}
                    sx={{
                      color: {
                        xs: `${sharedColors.newBlack} !important`,
                        md: `${sharedColors.black} !important`,
                      },
                    }}
                  >
                    {t("balconySqm")}
                  </NewText>
                </StyledFlex>
                <LargeDivider />
              </MiddleContent>
              <BottomContent hiddenMobile={true}>
                {apartment.entryDate && (
                  <>
                    <StyledFlex flexStart={true}>
                      <NewText
                        component={"p"}
                        desktopFontSize={18}
                        desktopLineHeight={"100%"}
                        mobileFontSize={16}
                        mobileLineHeight={"100%"}
                        desktopFontWeight={300}
                        sx={{
                          color: {
                            xs: `${sharedColors.newBlack} !important`,
                            md: `${sharedColors.black} !important`,
                          },
                        }}
                        columnMobile={true}
                      >
                        {t("entryDate")}
                      </NewText>

                      <NewText
                        component={"p"}
                        desktopFontSize={20}
                        desktopLineHeight={"100%"}
                        mobileFontSize={16}
                        mobileLineHeight={"100%"}
                        desktopFontWeight={700}
                        sx={{
                          color: {
                            xs: `${sharedColors.newBlack} !important`,
                            md: `${sharedColors.black} !important`,
                          },
                        }}
                        columnMobile={true}
                      >
                        {moment(apartment.entryDate) >= moment(new Date())
                          ? moment(apartment.entryDate).format("DD/MM/YYYY")
                          : t("immediateEntry")}
                      </NewText>
                    </StyledFlex>
                    <LargeDivider />
                  </>
                )}

                <StyledFlex>
                  <NewText
                    component={"p"}
                    desktopFontSize={18}
                    desktopLineHeight={"normal"}
                    mobileFontSize={16}
                    mobileLineHeight={"normal"}
                    desktopFontWeight={400}
                    sx={{
                      color: {
                        xs: `${sharedColors.newBlack} !important`,
                        md: `${sharedColors.black} !important`,
                      },
                    }}
                    columnMobile={true}
                  >
                    {t("rent")}
                  </NewText>
                  <NewText
                    component={"p"}
                    desktopFontSize={20}
                    desktopLineHeight={"1.2"}
                    mobileFontSize={16}
                    mobileLineHeight={"1.2"}
                    desktopFontWeight={700}
                    sx={{
                      color: {
                        xs: `${sharedColors.newBlack} !important`,
                        md: `${sharedColors.black} !important`,
                      },
                      whiteSpace: "nowrap",
                    }}
                    columnMobile={true}
                  >
                    {formattedPrice} {apartment.priceCurrency}
                  </NewText>
                </StyledFlex>
                {!isTheApartmentTaken ? (
                  <StyledButton variant="text">
                    {locale === "he" ? <ArrowLeftIcon /> : <ArrowRightIcon />}
                  </StyledButton>
                ) : (
                  <ButtonPlaceholder />
                )}
              </BottomContent>
            </ContentPart>
          </UpperBox>
          <BottomContent hiddenDesktop={true}>
            <StyledFlexRow>
              <NewText
                component={"p"}
                desktopFontSize={18}
                desktopLineHeight={"100%"}
                mobileFontSize={16}
                mobileLineHeight={"100%"}
                desktopFontWeight={300}
                sx={{
                  color: {
                    xs: `${sharedColors.newBlack} !important`,
                    md: `${sharedColors.black} !important`,
                  },
                }}
                columnMobile={true}
              >
                {t("entryDate")}:
              </NewText>
              {apartment.entryDate && (
                <NewText
                  component={"p"}
                  desktopFontSize={20}
                  desktopLineHeight={"100%"}
                  mobileFontSize={16}
                  mobileLineHeight={"100%"}
                  desktopFontWeight={700}
                  sx={{
                    color: {
                      xs: `${sharedColors.newBlack} !important`,
                      md: `${sharedColors.black} !important`,
                    },
                  }}
                  columnMobile={true}
                >
                  {moment(apartment.entryDate) >= moment(new Date())
                    ? moment(apartment.entryDate).format("DD/MM/YYYY")
                    : t("immediateEntry")}
                </NewText>
              )}
            </StyledFlexRow>
            <LargeDivider />
            <BottomContentMobileDivider />
            <StyledFlexRow>
              <NewText
                component={"p"}
                desktopFontSize={18}
                desktopLineHeight={"100%"}
                mobileFontSize={16}
                mobileLineHeight={"100%"}
                desktopFontWeight={300}
                sx={{
                  color: {
                    xs: `${sharedColors.newBlack} !important`,
                    md: `${sharedColors.black} !important`,
                  },
                }}
                columnMobile={true}
              >
                {t("rent")}:
              </NewText>
              <StyledFlexRow>
                <NewText
                  component={"p"}
                  desktopFontSize={20}
                  desktopLineHeight={"100%"}
                  mobileFontSize={16}
                  mobileLineHeight={"100%"}
                  desktopFontWeight={700}
                  sx={{
                    color: {
                      xs: `${sharedColors.newBlack} !important`,
                      md: `${sharedColors.black} !important`,
                    },
                    whiteSpace: "nowrap",
                  }}
                  columnMobile={true}
                >
                  {formattedPrice} {apartment.priceCurrency}
                </NewText>
                {!isTheApartmentTaken ? (
                  <StyledButton variant="text">
                    {locale === "he" ? <ArrowLeftIcon /> : <ArrowRightIcon />}
                  </StyledButton>
                ) : null}
              </StyledFlexRow>
            </StyledFlexRow>
          </BottomContent>
        </ItemBox>
      </StyledAccordionSummary>
      <StyledDrawer
        anchor={isDesktop ? "left" : "bottom"}
        open={open}
        transitionDuration={800}
        onClose={handleClose}
        sx={{ zIndex: 99999999 }}
        SlideProps={{
          easing: "cubic-bezier(0.25, 1, 0.5, 1)",
        }}
        ModalProps={{
          // Allow focus to leave drawer for external modals (e.g., BMBY scheduler)
          disableEnforceFocus: true,
        }}
        PaperProps={{
          sx: {
            width: { xs: "100%", md: 658 },
            height: { xs: "90%", md: "100%" },
            borderRadius: {
              xs: "16px 16px 0 0",
              md: 0,
            },
          },
        }}
      >
        <DrawerInner
          imageGallery={imageGallery}
          apartment={apartment}
          formSubjects={formSubjects}
          messages={messages}
          bmbyId={bmbyId}
          bmbyMeetingId={bmbyMeetingId}
          status={status}
          projectPage={true}
          companyId={companyId}
          handleDrawerClose={handleClose}
          locale={locale}
          buildingCharacteristicsText={buildingCharacteristicsText}
          fontFamilyVariable={fontFamilyVariable}
          phone={phone}
          rentProjectInterestedNum={rentProjectInterestedNum}
          rentalProjects={rentalProjects}
          cmsProjectLeadIds={cmsProjectLeadIds}
          preSelectedProject={preSelectedProject}
          leaveDetailsButtonText={leaveDetailsButtonText}
        />
      </StyledDrawer>
    </>
  )
}

export default ApartmentDrawerItem
